import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const DEFAULT_ADSENSE_CLIENT = "ca-pub-3265135514320410";
const DEFAULT_TRAINING_ADSENSE_SLOT = "8367936644";
const ADSENSE_SCRIPT_ID = "chesshood-adsense-script";
const AD_FILL_CHECK_TIMEOUT_MS = 12000;
const IS_LOCALHOST =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

function firstNonEmptyValue(...values) {
  return (
    values.find(
      (value) => typeof value === "string" && value.trim().length > 0
    ) || ""
  );
}

function readRuntimeConfig() {
  if (typeof window === "undefined") {
    return {};
  }

  return window.__CHESSHOOD_RUNTIME_CONFIG__ || {};
}

function readMetaContent(name) {
  if (typeof document === "undefined") {
    return "";
  }

  return document.querySelector(`meta[name="${name}"]`)?.content || "";
}

function resolveAdSenseConfig() {
  const runtimeConfig = readRuntimeConfig();

  return {
    client: firstNonEmptyValue(
      runtimeConfig.trainingAdSenseClient,
      runtimeConfig.adsenseClient,
      runtimeConfig.TRAINING_ADSENSE_CLIENT,
      runtimeConfig.ADSENSE_CLIENT,
      process.env.REACT_APP_TRAINING_ADSENSE_CLIENT,
      process.env.REACT_APP_ADSENSE_CLIENT,
      readMetaContent("google-adsense-account"),
      DEFAULT_ADSENSE_CLIENT
    ),
    slot: firstNonEmptyValue(
      runtimeConfig.trainingAdSenseSlot,
      runtimeConfig.adsenseSlot,
      runtimeConfig.TRAINING_ADSENSE_SLOT,
      runtimeConfig.ADSENSE_SLOT,
      process.env.REACT_APP_TRAINING_ADSENSE_SLOT,
      process.env.REACT_APP_ADSENSE_SLOT,
      DEFAULT_TRAINING_ADSENSE_SLOT
    ),
  };
}

function ensureAdSenseScript(client) {
  const existingScript =
    typeof document !== "undefined" &&
    document.querySelector(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
    );

  if (
    typeof document === "undefined" ||
    !client ||
    document.getElementById(ADSENSE_SCRIPT_ID) ||
    existingScript
  ) {
    return (
      document.getElementById(ADSENSE_SCRIPT_ID) ||
      existingScript ||
      null
    );
  }

  const script = document.createElement("script");
  script.id = ADSENSE_SCRIPT_ID;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
    client
  )}`;
  document.head.appendChild(script);
  return script;
}

export default function WebTrainingAdSlot({
  isPro,
  isDue = false,
  puzzlesUntilNextAd = 0,
  onRenderStateChange,
}) {
  const adRef = useRef(null);
  const [{ client: adSenseClient, slot: adSenseSlot }] = useState(() =>
    resolveAdSenseConfig()
  );
  const [isScriptReady, setIsScriptReady] = useState(() =>
    Boolean(typeof window !== "undefined" && window.adsbygoogle?.push)
  );
  const [adRenderState, setAdRenderState] = useState("idle");
  const hasAdSenseClient = Boolean(adSenseClient);
  const hasManualAdSenseSlot = Boolean(adSenseClient && adSenseSlot);
  const shouldUseMockAd = IS_LOCALHOST;
  const shouldShowCompactFallback =
    adRenderState === "no_fill" ||
    adRenderState === "script_error" ||
    adRenderState === "render_error" ||
    adRenderState === "script_wait_timeout";

  useEffect(() => {
    if (typeof onRenderStateChange === "function") {
      onRenderStateChange(adRenderState);
    }
  }, [adRenderState, onRenderStateChange]);

  useEffect(() => {
    if (
      isPro ||
      shouldUseMockAd ||
      !isDue ||
      !hasManualAdSenseSlot ||
      adRenderState !== "requested" ||
      !adRef.current
    ) {
      return;
    }

    const fillCheckTimer = window.setTimeout(() => {
      if (!adRef.current) {
        return;
      }

      const adStatus =
        adRef.current.getAttribute("data-ad-status") ||
        adRef.current.getAttribute("data-adsbygoogle-status") ||
        "";
      const hasIframe = Boolean(adRef.current.querySelector("iframe"));

      if (adStatus === "filled" || hasIframe) {
        setAdRenderState("filled");
        return;
      }

      if (adStatus === "unfilled" || (!hasIframe && adStatus !== "filled")) {
        setAdRenderState("no_fill");
      }
    }, AD_FILL_CHECK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(fillCheckTimer);
    };
  }, [
    adRenderState,
    hasManualAdSenseSlot,
    isDue,
    isPro,
    shouldUseMockAd,
  ]);

  useEffect(() => {
    if (isPro || !hasAdSenseClient || shouldUseMockAd) {
      return;
    }

    const markReady = () => {
      setIsScriptReady(true);
    };
    const markError = () => {
      setAdRenderState("script_error");
    };

    const script = ensureAdSenseScript(adSenseClient);
    if (window.adsbygoogle?.push) {
      markReady();
      return;
    }

    if (script) {
      script.addEventListener("load", markReady);
      script.addEventListener("error", markError);
    }

    const timer = window.setTimeout(() => {
      if (window.adsbygoogle?.push) {
        markReady();
      }
    }, 2500);

    return () => {
      window.clearTimeout(timer);
      if (script) {
        script.removeEventListener("load", markReady);
        script.removeEventListener("error", markError);
      }
    };
  }, [adSenseClient, hasAdSenseClient, isPro, shouldUseMockAd]);

  useEffect(() => {
    if (
      isPro ||
      shouldUseMockAd ||
      !isDue ||
      !hasManualAdSenseSlot ||
      !adRef.current
    ) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    setAdRenderState("loading");

    const requestAd = () => {
      if (cancelled || !adRef.current) {
        return;
      }

      if (!window.adsbygoogle?.push) {
        if (attempts < 20) {
          attempts += 1;
          window.setTimeout(requestAd, 500);
          return;
        }

        setAdRenderState("script_wait_timeout");
        return;
      }

      if (
        adRef.current.dataset.loaded === "1" ||
        adRef.current.getAttribute("data-adsbygoogle-status")
      ) {
        setAdRenderState("requested");
        return;
      }

      try {
        window.adsbygoogle.push({});
        adRef.current.dataset.loaded = "1";
        setAdRenderState("requested");
      } catch (error) {
        if (attempts < 10) {
          attempts += 1;
          window.setTimeout(requestAd, 400);
          return;
        }

        setAdRenderState("render_error");
        console.error("Training ad slot failed to render:", error);
      }
    };

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      requestAd();
    } catch (error) {
      setAdRenderState("render_error");
      console.error("Training ad slot failed to initialize:", error);
    }

    return () => {
      cancelled = true;
    };
  }, [
    hasManualAdSenseSlot,
    isDue,
    isPro,
    isScriptReady,
    shouldUseMockAd,
  ]);

  useEffect(() => {
    if (
      isPro ||
      shouldUseMockAd ||
      !isDue ||
      !hasAdSenseClient ||
      hasManualAdSenseSlot
    ) {
      return;
    }

    console.warn(
      "Training ad break reached, but no AdSense slot is configured. Set REACT_APP_TRAINING_ADSENSE_SLOT or REACT_APP_ADSENSE_SLOT."
    );
  }, [hasAdSenseClient, hasManualAdSenseSlot, isDue, isPro, shouldUseMockAd]);

  if (isPro) {
    return null;
  }

  return (
    <section className="ch-training-monetize">
      <div className="ch-training-monetize-kicker">Free plan</div>
      <h2 className="ch-training-monetize-title">
        {isDue ? "Advertisement" : "Free training with ads"}
      </h2>
      <p className="ch-training-monetize-copy">
        {isDue
          ? "This ad slot is now active for your free training session."
          : `Advertisement will appear after ${puzzlesUntilNextAd} more solved puzzle${
              puzzlesUntilNextAd === 1 ? "" : "s"
            }.`}{" "}
        Upgrade to Pro to remove ads from training and use the same account on
        web, iPhone and Android once your backend entitlement sync updates the
        profile.
      </p>

      {isDue && hasManualAdSenseSlot && !shouldUseMockAd ? (
        <div className="ch-training-ad-box">
          <div className="ch-training-ad-label">Advertisement</div>
          {!shouldShowCompactFallback ? (
            <ins
              ref={adRef}
              className="adsbygoogle"
              style={{ display: "block", width: "100%", minHeight: 250 }}
              data-ad-client={adSenseClient}
              data-ad-slot={adSenseSlot}
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
          ) : (
            <div
              style={{
                display: "grid",
                gap: 10,
                padding: 4,
              }}
            >
              <div
                style={{
                  padding: "14px 12px",
                  borderRadius: 14,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(242,184,77,0.16))",
                  border: "1px solid rgba(242,184,77,0.24)",
                }}
              >
                <strong style={{ display: "block", color: "#fff1cb" }}>
                  Sponsored: Chesshood Pro
                </strong>
                <span style={{ color: "#e6d5b3", lineHeight: 1.5 }}>
                  This ad request did not return a Google ad on this device, so
                  we are showing a compact fallback instead of an empty box.
                </span>
              </div>
            </div>
          )}
          {adRenderState !== "filled" && (
            <div style={{ marginTop: 10, color: "#cdbf9f", lineHeight: 1.5 }}>
              {adRenderState === "script_error" ||
              adRenderState === "render_error"
                ? "AdSense was configured, but this slot failed to initialize in the browser."
                : adRenderState === "no_fill"
                  ? "Google did not return an ad for this request yet. You can continue and try again on the next ad break."
                  : adRenderState === "requested"
                    ? "Ad request sent to Google. Waiting for the ad to load on this device..."
                    : adRenderState === "script_wait_timeout"
                      ? "The browser waited for AdSense too long, so the live ad did not become ready here."
                    : "Loading advertisement..."}
            </div>
          )}
        </div>
      ) : isDue && shouldUseMockAd ? (
        <div className="ch-training-ad-placeholder">
          <div className="ch-training-ad-label">Mock advertisement</div>
          <div
            style={{
              display: "grid",
              gap: 10,
              padding: 4,
            }}
          >
            <div
              style={{
                padding: "14px 12px",
                borderRadius: 14,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(242,184,77,0.16))",
                border: "1px solid rgba(242,184,77,0.24)",
              }}
            >
              <strong style={{ display: "block", color: "#fff1cb" }}>
                Sponsored: Chesshood Pro
              </strong>
              <span style={{ color: "#e6d5b3", lineHeight: 1.5 }}>
                This is the local dev ad mock. Live Google ads only render on an
                approved public domain, not on localhost.
              </span>
            </div>
            <div style={{ color: "#cdbf9f", lineHeight: 1.5 }}>
              Client: <code>{adSenseClient || "missing"}</code>
              <br />
              Slot: <code>{adSenseSlot || "missing"}</code>
            </div>
          </div>
        </div>
      ) : isDue ? (
        <div className="ch-training-ad-placeholder">
          <div className="ch-training-ad-label">
            {IS_LOCALHOST ? "Mock advertisement" : "Advertisement"}
          </div>
          <div
            style={{
              display: "grid",
              gap: 10,
              padding: 4,
            }}
          >
            <div
              style={{
                padding: "14px 12px",
                borderRadius: 14,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(242,184,77,0.16))",
                border: "1px solid rgba(242,184,77,0.24)",
              }}
            >
              <strong style={{ display: "block", color: "#fff1cb" }}>
                Sponsored: Chesshood Pro
              </strong>
              <span style={{ color: "#e6d5b3", lineHeight: 1.5 }}>
                Remove training ads, keep one Pro status across platforms, and
                unlock the cleaner experience.
              </span>
            </div>
            <div style={{ color: "#cdbf9f", lineHeight: 1.5 }}>
              {IS_LOCALHOST
                ? "Local dev uses a mock ad card here. Live Google ads still need an approved domain, and this training placement needs an AdSense slot id."
                : hasAdSenseClient
                  ? "The AdSense client is available, but this training placement still needs a slot id (`REACT_APP_TRAINING_ADSENSE_SLOT` or `REACT_APP_ADSENSE_SLOT`)."
                  : "Real Google ads need an AdSense client and slot configuration."}
            </div>
          </div>
        </div>
      ) : (
        <div className="ch-training-ad-placeholder">
          <div className="ch-training-ad-label">Advertisement</div>
          <div>
            Ad slot locked for now. Solve {puzzlesUntilNextAd} more puzzle
            {puzzlesUntilNextAd === 1 ? "" : "s"} to trigger the next ad break.
          </div>
          {!hasManualAdSenseSlot && (
            <div style={{ marginTop: 8 }}>
              Add `REACT_APP_TRAINING_ADSENSE_SLOT` or `REACT_APP_ADSENSE_SLOT`
              to enable a dedicated training ad slot here.
            </div>
          )}
        </div>
      )}

      <div className="ch-training-monetize-actions">
        <Link to="/checkout" className="ch-training-upgrade-link">
          Go Pro and remove ads
        </Link>
      </div>
    </section>
  );
}
